import { ArrowRight, Clock, Info, Loader2, Pencil } from "lucide-react";
import { Link, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useWorksite } from "@/features/canteiros/hooks/use-worksite.ts";
import { TechnicalManagerRow } from "@/features/rdo/components/technical-manager-row";
import { ObraNameRow } from "@/features/stages/components/obra-name-row";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

export function ConfiguracoesDaObraPage() {
	const { obra: obraKey } = useParams();
	const { has } = usePermissions();
	const {
		obra,
		obras,
		isLoading: obraLoading,
		isError: obraError,
		refetch: refetchObra,
	} = useActiveObra();
	const canteiroId = obra?.canteiroId;
	const {
		data: worksite,
		isLoading: worksiteLoading,
		isError: worksiteError,
	} = useWorksite(canteiroId);

	const shared = obras.filter((o) => o.canteiroId === canteiroId).length > 1;
	const isLoading = obraLoading || (!!canteiroId && worksiteLoading);

	if (isLoading) {
		return (
			<div className="page-loading">
				<Loader2 />
			</div>
		);
	}

	if (obraError || worksiteError || !obra || !worksite) {
		return (
			<Card>
				<CardContent className="card-state">
					<p>Não foi possível carregar os dados da obra.</p>
					<Button variant="outline" onClick={() => refetchObra()}>
						Tentar novamente
					</Button>
				</CardContent>
			</Card>
		);
	}

	const { address } = worksite;
	const hasAddress = Boolean(address.street || address.city || address.zipcode);

	return (
		<div className="obra-settings-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Configurações da obra</h1>
					<p className="page-subtitle">Dados gerais desta obra.</p>
				</div>
			</div>

			<Card className="obra-settings-card">
				<div className="obra-settings-rows">
					<ObraNameRow obra={obra} />
					<TechnicalManagerRow obra={obra} />

					<div className="obra-settings-row">
						<div className="obra-settings-row__body">
							<p className="obra-settings-row__label">
								{shared ? "Endereço do canteiro" : "Endereço"}
							</p>
							{hasAddress ? (
								<address className="obra-settings-address">
									<span>
										{address.street}
										{address.number ? `, ${address.number}` : ""}
										{address.complement ? ` — ${address.complement}` : ""}
									</span>
									{address.neighborhood && <span>{address.neighborhood}</span>}
									<span>
										{address.city}
										{address.state ? ` - ${address.state}` : ""}
									</span>
									{address.zipcode && <span>CEP {address.zipcode}</span>}
								</address>
							) : (
								<p className="obra-settings-row__value obra-settings-row__value--empty">
									Ainda não informado
								</p>
							)}

							{shared && (
								<p className="obra-settings-row__note">
									<Info />
									Este endereço vale para todas as obras deste canteiro.
								</p>
							)}
						</div>
						<Button asChild variant="outline" size="sm">
							<Link to={`/${obraKey}/endereco`}>
								<Pencil />
								Editar
							</Link>
						</Button>
					</div>
				</div>
			</Card>

			{has("time_settings:manage") && (
				<Link
					to={`/${obraKey}/configuracoes-da-obra/jornada`}
					className="settings-card-link group"
				>
					<Card className="settings-card">
						<CardHeader>
							<div className="settings-card__top">
								<div className="settings-card__icon">
									<Clock />
								</div>
								<ArrowRight className="settings-card__arrow" />
							</div>
							<CardTitle className="settings-card__title">
								Jornada e horas extras
							</CardTitle>
							<CardDescription>
								Horário de trabalho por dia, percentuais de hora extra e
								feriados do canteiro.
							</CardDescription>
						</CardHeader>
					</Card>
				</Link>
			)}
		</div>
	);
}
